import {
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, map, startWith } from 'rxjs/operators';
import { LayoutService } from './services/layout.service';
import { PageAction } from './interfaces/layout/page-action.interface';
import { ModusThemeConfig, ThemeService } from './services/theme.service';
import { AuthServiceBase } from './services/auth/auth.service.base';
import { UserAccessStore } from './services/user-access/user-access.store';
import { RegisteredApplicationStore } from './services/registered-applications/registered-application.store';
import { AgenticChatPanelComponent } from './components/agentic-chat-panel/agentic-chat-panel.component';
import { GlobalLoaderComponent } from './components/global-loader/global-loader.component';
import { ToastHostComponent } from './components/toast-host/toast-host.component';
import { UploadsTrayComponent } from './components/uploads-tray/uploads-tray.component';
import { AgenticChatService } from './services/agentic-chat/agentic-chat.service';
import { BreadcrumbTooltipsDirective } from './directives/breadcrumb-tooltips.directive';
import { generateTestId } from './utils/test-identifier.utils';
import {
  buildModusBreadcrumbItems,
  getBreadcrumbNavigationTarget,
  REGISTERED_APPLICATIONS_BREADCRUMB_FALLBACK_LABEL,
  REGISTERED_APPLICATIONS_BREADCRUMB_ROUTE,
} from './utils/modus-breadcrumb.utils';

function urlPathBeforeQuery(url: string): string {
  const segment = url.split('?', 1)[0];
  return segment.length > 0 ? segment : '/';
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    GlobalLoaderComponent,
    ToastHostComponent,
    UploadsTrayComponent,
    AgenticChatPanelComponent,
    BreadcrumbTooltipsDirective,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class App {
  private router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);
  layoutService = inject(LayoutService);
  themeService = inject(ThemeService);
  authService = inject(AuthServiceBase);
  agenticChat = inject(AgenticChatService);
  userAccessStore = inject(UserAccessStore);
  private registeredApplicationStore = inject(RegisteredApplicationStore);

  private readonly currentUrlPath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => urlPathBeforeQuery(this.router.url)),
      startWith(urlPathBeforeQuery(this.router.url))
    ),
    { initialValue: urlPathBeforeQuery(this.router.url) }
  );

  readonly headerAreaSubtitle = computed(() => {
    const path = this.currentUrlPath();
    const onProjectsWorkspace = path === '/projects' || path.startsWith('/projects/');
    if (!onProjectsWorkspace) {
      return null;
    }

    const pref = this.userAccessStore.preferredEntry();
    if (pref === 'developer') {
      return 'User';
    }
    if (pref === 'support') {
      return 'Support';
    }
    if (pref === 'admin') {
      return 'Admin';
    }

    const cap = this.userAccessStore.capabilities();
    if (cap?.isDeveloper && !cap?.isSupport && !cap?.isAdmin) {
      return 'User';
    }
    if (cap?.isSupport && !cap?.isDeveloper && !cap?.isAdmin) {
      return 'Support';
    }
    if (cap?.isAdmin && !cap?.isDeveloper && !cap?.isSupport) {
      return 'Admin';
    }

    return null;
  });

  readonly showChangeRoleInAccountMenu = computed(() => {
    if (!this.userAccessStore.hasBothRoles()) {
      return false;
    }
    return this.currentUrlPath() !== '/select-role';
  });

  private readonly supportWorkspaceRootCrumb = computed(() => {
    if (this.userAccessStore.preferredEntry() !== 'support') {
      return null;
    }

    const name = this.registeredApplicationStore.selected()?.name?.trim();
    return {
      label: name || REGISTERED_APPLICATIONS_BREADCRUMB_FALLBACK_LABEL,
      route: REGISTERED_APPLICATIONS_BREADCRUMB_ROUTE,
    };
  });

  readonly modusBreadcrumbItems = computed(() =>
    buildModusBreadcrumbItems(
      this.layoutService.pageInfo(),
      this.supportWorkspaceRootCrumb()
    )
  );

  isAccountMenuOpen = signal(false);

  onModusThemeChange(event: Event): void {
    this.themeService.syncFromModusChange(
      (event as CustomEvent<ModusThemeConfig>).detail
    );
  }

  goHome(): void {
    void this.router.navigateByUrl('/projects');
  }

  navigateTo(route: string): void {
    void this.router.navigateByUrl(route);
  }

  onBreadcrumbClick(event: Event): void {
    const crumb = (event as CustomEvent<{ label: string; route?: string }>).detail;
    const target = getBreadcrumbNavigationTarget(crumb);
    if (!target) {
      return;
    }

    const currentPath = urlPathBeforeQuery(this.router.url);
    if (currentPath === target) {
      return;
    }

    void this.router.navigateByUrl(target);
  }

  onAddClick(): void {
    this.layoutService.triggerAdd();
  }

  onAccountMenuVisibilityChange(event: Event): void {
    const isVisible = (event as CustomEvent<{ isVisible: boolean }>).detail.isVisible;
    this.isAccountMenuOpen.set(isVisible);
    if (isVisible) {
      this.dismissAccountMenuTooltip();
    }
  }

  private dismissAccountMenuTooltip(): void {
    this.host.nativeElement
      .querySelector('modus-wc-tooltip.account-menu-tooltip')
      ?.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
  }

  onAccountMenuItemSelect(event: Event): void {
    const value = (event as CustomEvent<{ value: string }>).detail.value;
    this.closeAccountMenu();

    if (value === 'change-role') {
      void this.router.navigateByUrl('/select-role');
      return;
    }

    if (value === 'logout') {
      void this.logout();
    }
  }

  closeAccountMenu(): void {
    this.isAccountMenuOpen.set(false);
  }

  openSelectRolePage(): void {
    this.closeAccountMenu();
    void this.router.navigateByUrl('/select-role');
  }

  logout(): void {
    this.authService
      .logout()
      .then(() => {
        this.userAccessStore.clear();
        this.registeredApplicationStore.clear();
        this.closeAccountMenu();
      })
      .catch((error) => {
        console.error('Logout failed:', error);
        this.closeAccountMenu();
      });
  }

  getTestId(section: string, elementType: string, actionOrPurpose: string, qualifier?: string): string {
    return generateTestId(`App${section}`, elementType, actionOrPurpose, qualifier);
  }

  getPageActionModusColor(action: PageAction): 'secondary' | 'warning' {
    if (action.buttonColor === 'retry') {
      return 'warning';
    }
    return 'secondary';
  }

  getPageActionVariant(action: PageAction): 'filled' | 'outline' {
    if (action.buttonColor === 'retry') {
      return 'filled';
    }
    return 'outline';
  }

  getPageActionCustomClass(action: PageAction): string | undefined {
    if (action.buttonColor === 'retry') {
      return 'retry-action';
    }
    return undefined;
  }
}
